import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { graphql } from "gql.tada";

// GraphQLスキーマから型をインポート
export type UserAnalyticsViewPresetID = ReturnType<
  typeof graphql.scalar<"UserAnalyticsViewPresetID">
>;

// プリセットのカテゴリタイプ
export type PresetCategory = "device" | "behavior" | "traffic";

// プリセット定義の型
interface PresetDefinition {
  label: ReturnType<typeof msg>;
  description: ReturnType<typeof msg>;
  category: PresetCategory;
}

// プリセット定義（Map形式で管理）
const UA_PRESET_CONFIG = new Map<UserAnalyticsViewPresetID, PresetDefinition>([
  [
    "PRESET_DESKTOP_ONLY",
    {
      label: msg`Desktop`,
      description: msg`Analyze user behavior and conversion trends for desktop traffic`,
      category: "device",
    },
  ],
  [
    "PRESET_NON_DESKTOP",
    {
      label: msg`Mobile & Tablet`,
      description: msg`Analyze user experience and engagement for mobile and tablet traffic`,
      category: "device",
    },
  ],
  [
    "PRESET_WITH_NAVIGATION",
    {
      label: msg`Sessions with surf activity`,
      description: msg`Analyze site surfing patterns for sessions with multiple page views`,
      category: "behavior",
    },
  ],
  [
    "PRESET_WITH_CONVERSION",
    {
      label: msg`CV Sessions`,
      description: msg`Analyze trends and performance for all key events across the site`,
      category: "behavior",
    },
  ],
  [
    "PRESET_GOOGLE_ORGANIC",
    {
      label: msg`Organic Search`,
      description: msg`Analyze search performance and keyword trends for traffic in the Organic Search default channel group`,
      category: "traffic",
    },
  ],
  [
    "PRESET_FIRST_SESSION",
    {
      label: msg`First Visit`,
      description: msg`Analyze behavior and engagement during first-time sessions`,
      category: "behavior",
    },
  ],
]);

// カテゴリ定義
const UA_PRESET_CATEGORIES = {
  device: {
    label: msg`By Device`,
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  },
  behavior: {
    label: msg`Site Behavior`,
    color:
      "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  },
  traffic: {
    label: msg`Traffic Source`,
    color:
      "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  },
} as const;

// プリセットのラベルを取得（i18n._()で翻訳）
export function getPresetLabel(
  id: UserAnalyticsViewPresetID,
  i18n: I18n,
): string {
  const config = UA_PRESET_CONFIG.get(id);
  return config ? i18n._(config.label) : id;
}

// プリセットの説明を取得（i18n._()で翻訳）
export function getPresetDescription(
  id: UserAnalyticsViewPresetID,
  i18n: I18n,
): string {
  const config = UA_PRESET_CONFIG.get(id);
  return config ? i18n._(config.description) : "";
}

// プリセット情報を一括取得（ダイアログで使用）
export function getPresetInfo(
  id: UserAnalyticsViewPresetID,
  i18n: I18n,
):
  | {
      id: UserAnalyticsViewPresetID;
      label: string;
      description: string;
      category: PresetCategory;
      categoryLabel: string;
      categoryColor: string;
    }
  | undefined {
  const config = UA_PRESET_CONFIG.get(id);
  if (!config) return undefined;

  const category = config.category;
  const categoryDef = UA_PRESET_CATEGORIES[category];

  return {
    id,
    label: i18n._(config.label),
    description: i18n._(config.description),
    category,
    categoryLabel: i18n._(categoryDef.label),
    categoryColor: categoryDef.color,
  };
}

// すべてのプリセット情報を取得（ViewPreset型として）
export function getAllPresetsAsViewPresets(i18n: I18n): Array<{
  presetID: UserAnalyticsViewPresetID;
  name: string;
  description: string;
}> {
  return Array.from(UA_PRESET_CONFIG.entries()).map(([id, config]) => ({
    presetID: id,
    name: i18n._(config.label),
    description: i18n._(config.description),
  }));
}
